import {
  DEFAULT_TIME_REPORT_CONFIG,
  TimeReportConfigSchema,
  type TimeReportConfig,
  type TimeReportGroupByLevel,
  type TimeReportMetric,
} from '@hygge/shared';
import type { TimeReportResultRow } from '@/lib/time-entries';
import { escapeHtml, formatDuration, formatHours } from './report-print';

/**
 * Everything the Builder screen knows how to decide, without React.
 *
 * The screen is a form over ONE object — a `TimeReportConfig` — and every edit
 * it makes is a pure function here. That is not tidiness: the same object is
 * the REST body, the stored row and the model's output contract, so the rules
 * about it (a filter is empty or absent, never both; at most two grouping
 * levels; at most one time axis) have to hold in one place a test can reach.
 * The screen holds the object and nothing else.
 */

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export type ReportFilters = TimeReportConfig['filters'];
export type ReportFilterKey = keyof ReportFilters;

export interface ReportFilterDescriptor {
  key: ReportFilterKey;
  label: string;
  /** A multi-select of ids or enum values, or a single choice. */
  kind: 'list' | 'choice';
  /** `useFilterOptions` source key — the registry in components/filters. */
  source?:
    | 'projects'
    | 'employees'
    | 'departments'
    | 'positions'
    | 'employmentTypes'
    | 'projectManagers';
  /** Static options, for the two enum lists and the two scalar choices. */
  choices?: Array<{ value: string; label: string }>;
  placeholder: string;
  /** On screen from the start, never behind "Add filter". */
  pinned: boolean;
  /**
   * Reads a person's CURRENT department / position / employment type. There is
   * no history table for any of them, so a report carrying one of these is
   * counting hours where somebody sits today (design §6) and has to say so.
   */
  peopleAttribute: boolean;
}

/**
 * The nine filters, in the order they appear on screen.
 *
 * Only project and person are pinned, per §8: nine controls at once is a wall,
 * and the rest arrive through "Add filter" — or expanded, when a saved
 * configuration already uses them. `billable` is not pinned even though every
 * stored config spells it: 'all' is not a constraint, so showing the control
 * permanently would give a filter that constrains nothing the same weight as
 * one that does.
 */
export const REPORT_FILTERS: ReportFilterDescriptor[] = [
  {
    key: 'projectIds',
    label: 'Projects',
    kind: 'list',
    source: 'projects',
    placeholder: 'All projects',
    pinned: true,
    peopleAttribute: false,
  },
  {
    key: 'employeeIds',
    label: 'People',
    kind: 'list',
    source: 'employees',
    placeholder: 'Everyone',
    pinned: true,
    peopleAttribute: false,
  },
  {
    key: 'billable',
    label: 'Billable',
    kind: 'choice',
    choices: [
      { value: 'all', label: 'Billable and not' },
      { value: 'only', label: 'Billable only' },
      { value: 'exclude', label: 'Not billable' },
    ],
    placeholder: 'Billable and not',
    pinned: false,
    peopleAttribute: false,
  },
  {
    key: 'departmentIds',
    label: 'Departments',
    kind: 'list',
    source: 'departments',
    placeholder: 'All departments',
    pinned: false,
    peopleAttribute: true,
  },
  {
    key: 'positionIds',
    label: 'Positions',
    kind: 'list',
    source: 'positions',
    placeholder: 'All positions',
    pinned: false,
    peopleAttribute: true,
  },
  {
    key: 'employmentTypeIds',
    label: 'Employment types',
    kind: 'list',
    source: 'employmentTypes',
    placeholder: 'All employment types',
    pinned: false,
    peopleAttribute: true,
  },
  {
    key: 'projectStatuses',
    label: 'Project status',
    kind: 'list',
    choices: [
      { value: 'active', label: 'Active' },
      { value: 'on_hold', label: 'On hold' },
      { value: 'archived', label: 'Archived' },
    ],
    placeholder: 'Any status',
    pinned: false,
    peopleAttribute: false,
  },
  {
    key: 'projectManagerIds',
    label: 'Project manager',
    kind: 'list',
    // NOT the generic `managers` source, which is every active employee. This
    // filter compiles into an EXISTS over `project_members.is_manager`, so a
    // list that offers anybody else offers a selection whose only possible
    // answer is zero hours — and a zero reads as a fact. See
    // `components/filters/lib/project-managers.ts`.
    source: 'projectManagers',
    placeholder: 'Any manager',
    pinned: false,
    peopleAttribute: false,
  },
  {
    key: 'entrySources',
    label: 'Entry source',
    kind: 'list',
    // Kept equal to TimeReportEntrySourceEnum. A value the schema accepts and
    // this list omits is unreachable from the builder — the report can only be
    // asked for it through the AI draft path or a hand-edited saved config,
    // which is a filter that exists but cannot be used.
    choices: [
      { value: 'manual', label: 'Entered by hand' },
      { value: 'timer', label: 'Recorded by the timer' },
      { value: 'clockify', label: 'Imported from Clockify' },
      { value: 'tempo', label: 'Imported from Jira Tempo' },
      { value: 'paper', label: 'Tracked in Paper' },
    ],
    placeholder: 'Any source',
    pinned: false,
    peopleAttribute: false,
  },
  {
    key: 'hasTask',
    label: 'Task attached',
    kind: 'choice',
    choices: [
      { value: 'all', label: 'With and without a task' },
      { value: 'only', label: 'Only with a task' },
      { value: 'exclude', label: 'Only without a task' },
    ],
    placeholder: 'With and without a task',
    pinned: false,
    peopleAttribute: false,
  },
];

const FILTER_BY_KEY = new Map(REPORT_FILTERS.map((filter) => [filter.key, filter]));

export function filterDescriptor(key: ReportFilterKey): ReportFilterDescriptor {
  const descriptor = FILTER_BY_KEY.get(key);
  if (!descriptor) throw new Error(`No such report filter: ${key}`);
  return descriptor;
}

/**
 * Does this filter narrow the report?
 *
 * An absent list and an empty one are the same answer, deliberately: the draft
 * flow empties a list whenever a name matched nothing or matched two things, so
 * "empty" is a routine state and must not read as a constraint. Both scalars
 * treat 'all' — and, for `hasTask`, absence — the same way.
 */
export function isFilterActive(filters: ReportFilters, key: ReportFilterKey): boolean {
  const value = filters[key];
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value !== 'all';
  return false;
}

/** Every filter the configuration actually uses, in screen order. */
export function activeFilterKeys(config: TimeReportConfig): ReportFilterKey[] {
  return REPORT_FILTERS.filter((filter) => isFilterActive(config.filters, filter.key)).map(
    (filter) => filter.key,
  );
}

/**
 * What is on screen: the pinned pair, everything the configuration uses, and
 * anything opened by hand.
 *
 * The middle clause is the one that matters — a report loaded from a saved
 * configuration shows every constraint shaping its numbers, expanded. A hidden
 * filter is a number nobody can explain.
 */
export function visibleFilterKeys(
  config: TimeReportConfig,
  opened: ReportFilterKey[],
): ReportFilterKey[] {
  const openedSet = new Set(opened);
  return REPORT_FILTERS.filter(
    (filter) =>
      filter.pinned || openedSet.has(filter.key) || isFilterActive(config.filters, filter.key),
  ).map((filter) => filter.key);
}

/** What "Add filter" may still offer. */
export function availableFilterKeys(
  config: TimeReportConfig,
  opened: ReportFilterKey[],
): ReportFilterKey[] {
  const visible = new Set(visibleFilterKeys(config, opened));
  return REPORT_FILTERS.filter((filter) => !visible.has(filter.key)).map((filter) => filter.key);
}

/**
 * Remove a filter's constraint.
 *
 * A list is deleted rather than emptied, and `hasTask` is deleted rather than
 * set to 'all', so clearing a filter cannot write a key into a saved config
 * that nobody configured. `billable` is the exception the schema already makes:
 * it always carries a value, and 'all' is that value.
 */
export function clearFilter(filters: ReportFilters, key: ReportFilterKey): ReportFilters {
  const next: ReportFilters = { ...filters };
  if (key === 'billable') {
    next.billable = 'all';
    return next;
  }
  delete next[key];
  return next;
}

/**
 * Turn the completed-periods switch on or off.
 *
 * Off REMOVES the key rather than storing a `false`: a saved report carries the
 * constraints it was given and nothing else, which is the same asymmetry
 * `fromFilterState` applies to an emptied list. Here rather than in the screen
 * because the rule is the config's, not the button's — and because a rule living
 * in a JSX handler is a rule this repo cannot test. [HW-850]
 */
export function setCompletedPeriodsOnly(filters: ReportFilters, on: boolean): ReportFilters {
  const next: ReportFilters = { ...filters };
  if (on) next.completedPeriodsOnly = true;
  else delete next.completedPeriodsOnly;
  return next;
}

// ---------------------------------------------------------------------------
// The bridge to FilterProvider
// ---------------------------------------------------------------------------

/** The filter slice as `FilterProvider` holds it: every key present, lists always arrays. */
export type ReportFilterState = Record<string, string[] | string>;

/**
 * Config → provider state.
 *
 * Absent keys are spelled out because `FilterProvider` counts a value differing
 * from its default as an active filter, and `undefined !== 'all'` — an absent
 * `hasTask` would otherwise be reported as a filter somebody set.
 */
export function toFilterState(filters: ReportFilters): ReportFilterState {
  const state: ReportFilterState = {};
  for (const descriptor of REPORT_FILTERS) {
    const value = filters[descriptor.key];
    if (descriptor.kind === 'choice') {
      state[descriptor.key] = typeof value === 'string' ? value : 'all';
    } else {
      state[descriptor.key] = Array.isArray(value) ? [...value] : [];
    }
  }
  return state;
}

/**
 * Provider state → config, dropping everything that constrains nothing.
 *
 * The asymmetry with `toFilterState` is the point: 'all' and `[]` go IN so the
 * controls have something to render, and never come back OUT, so a report saved
 * after a glance at the filter row is byte-identical to the one that was
 * loaded.
 *
 * `current` supplies the keys the chip system does not own — today only
 * `completedPeriodsOnly`, a switch over the whole report rather than a criterion
 * with values. It is a required parameter and not a spread at the call site
 * because this function builds the object from nothing: anything it is not
 * handed is silently dropped, and a report that quietly resumed counting the
 * running month would change its own numbers with nobody asking. A required
 * argument is the one form of that reminder a later edit cannot walk past.
 */
export function fromFilterState(
  state: ReportFilterState,
  current: ReportFilters,
): ReportFilters {
  const filters: ReportFilters = { billable: 'all' };
  // Carried, never defaulted: a `false` written here would be a constraint the
  // report never configured, which is the thing this function exists to avoid.
  if (current.completedPeriodsOnly) filters.completedPeriodsOnly = true;
  for (const descriptor of REPORT_FILTERS) {
    const value = state[descriptor.key];
    if (descriptor.key === 'billable') {
      filters.billable = (typeof value === 'string' ? value : 'all') as ReportFilters['billable'];
      continue;
    }
    if (descriptor.kind === 'choice') {
      if (typeof value === 'string' && value !== 'all') {
        filters.hasTask = value as ReportFilters['hasTask'];
      }
      continue;
    }
    if (Array.isArray(value) && value.length > 0) {
      // The enum lists are typed narrower than string[]; the provider cannot
      // know that, and the schema re-checks every value server-side.
      (filters as Record<string, unknown>)[descriptor.key] = [...value];
    }
  }
  return filters;
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export type ReportTimeAxis = 'none' | 'day' | 'week';
export type ReportGroupLevel = Exclude<TimeReportGroupByLevel, 'day' | 'week'>;

/** Day and week are one axis offered as one choice; the other six combine freely. */
export interface GroupingState {
  levels: ReportGroupLevel[];
  axis: ReportTimeAxis;
}

export const MAX_GROUP_LEVELS = 2;

export const GROUP_LEVELS: Array<{ level: ReportGroupLevel; label: string }> = [
  { level: 'project', label: 'Project' },
  { level: 'employee', label: 'Person' },
  { level: 'department', label: 'Department' },
  { level: 'position', label: 'Position' },
  { level: 'employmentType', label: 'Employment type' },
  { level: 'task', label: 'Task' },
];

const LEVEL_LABELS: Record<TimeReportGroupByLevel, string> = {
  project: 'Project',
  employee: 'Person',
  day: 'Day',
  week: 'Week',
  department: 'Department',
  position: 'Position',
  employmentType: 'Employment type',
  task: 'Task',
};

export function levelLabel(level: TimeReportGroupByLevel): string {
  return LEVEL_LABELS[level];
}

function isTimeAxis(level: TimeReportGroupByLevel): level is 'day' | 'week' {
  return level === 'day' || level === 'week';
}

export function groupingOf(groupBy: TimeReportGroupByLevel[]): GroupingState {
  const axis = groupBy.find(isTimeAxis) ?? 'none';
  return {
    levels: groupBy.filter((level): level is ReportGroupLevel => !isTimeAxis(level)),
    axis,
  };
}

/**
 * The stored `groupBy`, time axis innermost.
 *
 * "Project, then week" is how a report of this shape is read aloud, and the
 * outer→inner order is what the table's columns follow.
 */
export function groupByOf(state: GroupingState): TimeReportGroupByLevel[] {
  return state.axis === 'none' ? [...state.levels] : [...state.levels, state.axis];
}

function levelCount(state: GroupingState): number {
  return state.levels.length + (state.axis === 'none' ? 0 : 1);
}

/**
 * Add or remove one non-time level.
 *
 * Both refusals are deliberate. A third level is refused rather than pushing
 * one out, because silently dropping a dimension the reader chose is how a
 * table starts answering a different question; and the last level cannot be
 * removed while nothing else groups the report, because `groupBy` may not be
 * empty and an empty table is not what "unticking the last box" should mean.
 */
export function toggleLevel(state: GroupingState, level: ReportGroupLevel): GroupingState {
  if (state.levels.includes(level)) {
    if (levelCount(state) <= 1) return state;
    return { ...state, levels: state.levels.filter((existing) => existing !== level) };
  }
  if (levelCount(state) >= MAX_GROUP_LEVELS) return state;
  return { ...state, levels: [...state.levels, level] };
}

/** Whether a time axis can be added at all — false once two levels are chosen. */
export function canSetTimeAxis(state: GroupingState): boolean {
  return state.axis !== 'none' || levelCount(state) < MAX_GROUP_LEVELS;
}

export function setTimeAxis(state: GroupingState, axis: ReportTimeAxis): GroupingState {
  if (axis === state.axis) return state;
  // Clearing can only shrink the grouping — unless it would leave nothing.
  if (axis === 'none') {
    return state.levels.length === 0 ? state : { ...state, axis };
  }
  if (!canSetTimeAxis(state)) return state;
  return { ...state, axis };
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const REPORT_METRICS: Array<{ metric: TimeReportMetric; label: string }> = [
  { metric: 'hours', label: 'Hours' },
  { metric: 'share', label: 'Share' },
  { metric: 'dailyAverage', label: 'Daily average' },
];

/** Add or drop a column, never leaving a table with no numbers in it. */
export function toggleMetric(
  metrics: TimeReportMetric[],
  metric: TimeReportMetric,
): TimeReportMetric[] {
  if (metrics.includes(metric)) {
    if (metrics.length <= 1) return metrics;
    return metrics.filter((existing) => existing !== metric);
  }
  // Kept in the declared order rather than appended, so the columns do not
  // reorder themselves according to which box was ticked first.
  return REPORT_METRICS.map((entry) => entry.metric).filter(
    (candidate) => candidate === metric || metrics.includes(candidate),
  );
}

// ---------------------------------------------------------------------------
// Row labels
// ---------------------------------------------------------------------------

const RESIDUAL_LABELS: Partial<Record<TimeReportGroupByLevel, string>> = {
  task: 'No task',
  department: 'No department',
  position: 'No position',
  employmentType: 'No employment type',
};

/**
 * What one cell of a grouping column says.
 *
 * Four of the eight levels are nullable columns, so an unlabelled key is a real
 * group — 23.5% of the entries in the live data have no task — and it is named
 * rather than left blank, so a reader can tell a residual bucket from a
 * rendering fault.
 *
 * The two time axes come back from the server as raw keys — `labelFor` says in
 * as many words that the screen decides how to print them — so this is where
 * that decision is made. A day is written the way the heading above it writes
 * dates, and a week says it is a week: `2026-08-03` beside 152 hours is
 * unreadable in a document where every other date says "Aug 3, 2026", and
 * nothing else on the row separates a Monday from the week it opens.
 */
export function rowLabel(level: TimeReportGroupByLevel, label: string): string {
  if (!label) return RESIDUAL_LABELS[level] ?? '—';
  if (level === 'day') return formatDay(label);
  if (level === 'week') {
    const day = formatDay(label);
    return day === label ? label : `Week of ${day}`;
  }
  return label;
}

// ---------------------------------------------------------------------------
// People attributes have no history (design §6)
// ---------------------------------------------------------------------------

export const PEOPLE_ATTRIBUTE_NOTICE =
  'Department, position and employment type describe people as they are today: hours count against where somebody sits now, not where they sat when they logged them.';

/**
 * Said on screen AND in the export, from one constant.
 *
 * A reader holding the printed document cannot ask the form why a report of
 * "this month" is headed Jul 27 — Sep 6, so the sentence has to travel with the
 * dates rather than stay on the screen that produced them.
 */
export const WEEK_WINDOW_NOTICE =
  'Grouping by week counts whole ISO weeks, so this window is wider than the period that was asked for — the total describes the dates printed beside it.';

/**
 * The same notice for a report whose window the completed-periods filter then
 * cut back.
 *
 * The plain sentence says the window is WIDER than the period asked for, which
 * stops being true the moment the cut clips its end: a week-grouped August can
 * end up narrower at the end than the request and wider at the start, and the
 * last bucket is a part-week. Saying that plainly beats a sentence that is half
 * right. [HW-850]
 */
export const WEEK_WINDOW_CUT_NOTICE =
  'Grouping by week counts whole ISO weeks, so this window starts earlier than the period that was asked for and ends where the completed-periods filter cut it — the last week counts only the days before that cut.';

const PEOPLE_LEVELS = new Set<TimeReportGroupByLevel>(['department', 'position', 'employmentType']);

export function usesPeopleAttributes(config: TimeReportConfig): boolean {
  const filtered = REPORT_FILTERS.some(
    (filter) => filter.peopleAttribute && isFilterActive(config.filters, filter.key),
  );
  return filtered || config.groupBy.some((level) => PEOPLE_LEVELS.has(level));
}

// ---------------------------------------------------------------------------
// Saved reports
// ---------------------------------------------------------------------------

export interface SavedReportLike {
  id: string;
  name: string;
  scope: 'personal' | 'shared';
  ownerId: string;
  config: unknown;
  owner?: { id: string; firstName: string; lastName: string };
}

/**
 * Mine and Shared, and a report I published appears only under Mine.
 *
 * Listing it twice would be two rows with one identity: renaming one and not
 * the other is impossible, so the duplicate could only ever confuse.
 */
export function splitSavedReports<T extends SavedReportLike>(
  reports: T[],
  meEmployeeId: string | undefined,
): { mine: T[]; shared: T[] } {
  const mine: T[] = [];
  const shared: T[] = [];
  for (const report of reports) {
    if (meEmployeeId && report.ownerId === meEmployeeId) mine.push(report);
    else shared.push(report);
  }
  return { mine, shared };
}

/**
 * The same rule `SavedReportsService.assertManageable` enforces.
 *
 * Gated in the UI because the Export template list is not: it renders Edit and
 * Delete on everyone's shared cards and lets the user find out about the 403
 * after pressing Save.
 */
export function canManageReport(
  report: SavedReportLike,
  meEmployeeId: string | undefined,
  canShare: boolean,
): boolean {
  if (meEmployeeId && report.ownerId === meEmployeeId) return true;
  return report.scope === 'shared' && canShare;
}

export function ownerName(report: SavedReportLike): string {
  if (!report.owner) return 'another person';
  return `${report.owner.firstName} ${report.owner.lastName}`.trim() || 'another person';
}

export type ParsedSavedConfig =
  | { ok: true; config: TimeReportConfig }
  | { ok: false; message: string };

/**
 * A stored configuration, checked before it reaches the form.
 *
 * There is no fallback to the default config on failure: running a different
 * report under a saved report's name is worse than saying the saved one is
 * broken.
 */
export function parseSavedConfig(raw: unknown): ParsedSavedConfig {
  const parsed = TimeReportConfigSchema.safeParse(raw);
  if (parsed.success) return { ok: true, config: parsed.data };
  const message = parsed.error.issues
    .map((issue) => `${issue.path.join('.') || 'config'}: ${issue.message}`)
    .join('; ');
  return { ok: false, message };
}

/** `DEFAULT_TIME_REPORT_CONFIG` is frozen, arrays included — copy before editing. */
export function cloneConfig(config: TimeReportConfig): TimeReportConfig {
  return {
    period: { ...config.period },
    filters: { ...config.filters },
    groupBy: [...config.groupBy],
    metrics: [...config.metrics],
    view: config.view,
  };
}

/**
 * Two configurations are the same report, whatever order their keys were
 * written in.
 *
 * `JSON.stringify` is not this: a config that came back from Zod spells its
 * filters in the schema's order and one rebuilt from the filter row spells them
 * in the screen's, so comparing the strings reports a change after ticking and
 * unticking one checkbox — and the "these numbers are out of date" notice ends
 * up sitting over numbers that are perfectly current. Array order is left
 * alone, because there it is meaning: `groupBy` reads outer → inner.
 */
export function configFingerprint(config: TimeReportConfig): string {
  return stableStringify(config);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      // An absent key and a key holding `undefined` are the same configuration;
      // `hasTask` is routinely one or the other.
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

// ---------------------------------------------------------------------------
// The constraints a report is under, in words
// ---------------------------------------------------------------------------

/** Option lists the screen has already loaded, keyed the way descriptors are. */
export type FilterOptionLabels = Partial<
  Record<NonNullable<ReportFilterDescriptor['source']>, ReadonlyMap<string, string>>
>;

export interface AppliedFilter {
  key: ReportFilterKey;
  /** "Employment types" — the same word the form uses. */
  label: string;
  /** What it narrows to, spelled out. */
  text: string;
}

/**
 * Every constraint shaping a report's numbers, named.
 *
 * The screen can get away with less — the form sits above the table — but the
 * export leaves the building alone, and a document headed "Time report by
 * department" over a total narrowed to two employment types is a number nobody
 * can audit. So this is derived from the CONFIG rather than from the form,
 * which means it cannot fall behind a filter somebody added.
 *
 * An id the loaded option lists cannot name is COUNTED rather than dropped: two
 * of the five employment types in the live database are inactive and never
 * reach the picker, and a saved report may be narrowed by one of them.
 * "1 not in the current list" is half an answer; silence is a wrong one.
 */
export function describeFilters(
  config: TimeReportConfig,
  labels: FilterOptionLabels = {},
): AppliedFilter[] {
  const applied: AppliedFilter[] = [];

  // First, because it is the strongest claim the document makes about its own
  // completeness. Without it a report carrying only this switch reached the
  // export as "No filters — every hour logged in this window" over a total that
  // had dropped the running month. It is not a chip, so the loop below — which
  // walks the chip descriptors — cannot see it. [HW-850]
  if (config.filters.completedPeriodsOnly) {
    // Spelled exactly as the control is, so a reader matching a line in the PDF
    // back to the screen does not have to guess that two phrasings are one thing.
    applied.push({ key: 'completedPeriodsOnly', label: 'Completed periods only', text: 'On' });
  }

  for (const descriptor of REPORT_FILTERS) {
    if (!isFilterActive(config.filters, descriptor.key)) continue;
    const value = config.filters[descriptor.key];

    if (typeof value === 'string') {
      applied.push({
        key: descriptor.key,
        label: descriptor.label,
        text: choiceLabel(descriptor, value),
      });
      continue;
    }
    if (!Array.isArray(value)) continue;

    const named: string[] = [];
    let unnamed = 0;
    for (const item of value) {
      const label = descriptor.choices
        ? choiceLabel(descriptor, item)
        : descriptor.source
          ? labels[descriptor.source]?.get(item)
          : undefined;
      if (label) named.push(label);
      else unnamed += 1;
    }

    const parts = [...named];
    if (unnamed > 0) parts.push(`${unnamed} not in the current list`);
    applied.push({ key: descriptor.key, label: descriptor.label, text: parts.join(', ') });
  }

  return applied;
}

function choiceLabel(descriptor: ReportFilterDescriptor, value: string): string {
  return descriptor.choices?.find((choice) => choice.value === value)?.label ?? value;
}

// ---------------------------------------------------------------------------
// The window and the totals
// ---------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * One calendar day, written as the reader reads dates.
 *
 * Assembled from the string's own parts rather than through `new Date(value)`:
 * that parses a bare date as UTC midnight and then prints it in local time, so
 * every date in the report would slip a day west of Greenwich.
 */
function formatDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const [, year, month, date] = match;
  return `${MONTHS[Number(month) - 1] ?? month} ${Number(date)}, ${year}`;
}

/**
 * The window a result was computed over.
 *
 * Always printed from `result.window`, never from the period that was asked
 * for: with a week axis the server grows the window to whole ISO weeks, so a
 * heading built from the request would sit above a total it does not describe.
 */
export function formatWindow(window: { from: string; to: string }): string {
  // "Completed periods only" over a period entirely inside the running month
  // counts nothing, and the counted window ends before it starts. Printing the
  // pair as a range would read as a typo; the report says plainly that there
  // was nothing to count, and the notice beside it says why. [HW-850]
  if (window.to < window.from) return `${formatDay(window.from)} — nothing completed yet`;
  return `${formatDay(window.from)} — ${formatDay(window.to)}`;
}

/**
 * The outer keys that have a total line above their rows.
 *
 * Under such a line the rows do not repeat the group's name: the reader has
 * just read it in the total line, one row up, and repeating it four times
 * beneath is what made the old table a wall — Tempo prints the person once and
 * indents the projects. The server puts a line over every group of a two-level
 * report, a group of one row included, so on the screens every row of such a
 * report is under a header; the set is still computed rather than assumed, so
 * a row that somehow arrived without one keeps its name instead of becoming an
 * orphan with no label anywhere. Spreadsheets keep the name on every row
 * regardless, because a filter on that column, or a pivot built from it, needs
 * the value present in the cell. [HW-877]
 */
export function headedGroups(rows: readonly TimeReportResultRow[]): ReadonlySet<string> {
  return new Set(rows.filter((row) => row.subtotal).map((row) => row.keys[0]));
}

/** Whether this row sits under a total line and should not repeat its name. */
export function underHeader(row: TimeReportResultRow, headed: ReadonlySet<string>): boolean {
  return !row.subtotal && headed.has(row.keys[0]);
}

/**
 * Whether a grouping cell is printed empty — deliberately, never as "No
 * project": a total's inner cell (there is no inner group) and a nested row's
 * outer cell (the name is one row up, in the total line). The screen, the PDF
 * and the spreadsheet all call this — the spreadsheet with `nested` false,
 * because it keeps the name on every row — so the rule is spelled once, not
 * three times. [HW-877]
 */
export function blankCell(row: TimeReportResultRow, nested: boolean, index: number): boolean {
  return (row.subtotal && index > 0) || (nested && index === 0);
}

/**
 * The report's own hours — its rows, not its group headers.
 *
 * A two-level report carries a total line per outer group, and those hours are
 * counted again in the rows below each one. Adding the list whole doubles the
 * report, which would make `totalsAgree` report a disagreement on every
 * two-level run. [HW-877]
 */
export function rowsSumSeconds(rows: TimeReportResultRow[]): number {
  return rows.reduce((total, row) => (row.subtotal ? total : total + row.totalSeconds), 0);
}

/**
 * Do the rows add up to the headline?
 *
 * Every query path in the runner derives from one predicate, and a filter
 * reaching one path and not another is precisely how the headline stops
 * agreeing with the axis below it. The rows are leaves of one aggregation, so
 * this must hold exactly; when it does not, the screen says so rather than
 * letting the reader trust a total that no longer describes the table.
 */
export function totalsAgree(rows: TimeReportResultRow[], totalSeconds: number): boolean {
  return rowsSumSeconds(rows) === totalSeconds;
}

export interface OuterTotal {
  key: string;
  label: string;
  totalSeconds: number;
}

/**
 * A two-level report folded onto its outer level, largest first, residual last.
 *
 * The chart plots this rather than the leaves: forty child rows on one axis is
 * a texture, not a comparison. The residual sorts last for the same reason it
 * does in the table — it is a remainder, not a competitor.
 */
export function outerTotals(
  rows: TimeReportResultRow[],
  outerLevel: TimeReportGroupByLevel,
): OuterTotal[] {
  const byKey = new Map<string, OuterTotal>();
  for (const row of rows) {
    // The server's own group total is skipped rather than used, even though it
    // is the same number: this function also serves one-level reports, which
    // have no such rows, so folding the leaves is the one rule that covers
    // both. Reading the header where there is one and summing where there is
    // not would be two rules and a bar chart that disagrees with itself.
    if (row.subtotal) continue;
    const key = row.keys[0] ?? '';
    const existing = byKey.get(key);
    if (existing) {
      existing.totalSeconds += row.totalSeconds;
      continue;
    }
    byKey.set(key, {
      key,
      label: rowLabel(outerLevel, row.label[0] ?? ''),
      totalSeconds: row.totalSeconds,
    });
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.key === '') return 1;
    if (b.key === '') return -1;
    return b.totalSeconds - a.totalSeconds;
  });
}

/** The bars are a comparison, not a listing — past a dozen they stop being one. */
export const CHART_ROWS = 12;

export interface ChartSeries {
  bars: OuterTotal[];
  /** Groups the chart left out. Zero means the chart is the whole report. */
  hiddenCount: number;
}

/**
 * The outer level as bars, ordered the way the table beside it is ordered.
 *
 * Three rules, each one a defect that reached a screen:
 *
 * A time axis is drawn in TIME order and never truncated. The server orders a
 * time axis by key, so a magnitude-sorted chart of weeks sat above a
 * chronological table of the same weeks; and slicing a year of weeks to twelve
 * turned a trend into a ranking with forty-one weeks missing. A trend that
 * drops periods is not a trend.
 *
 * The residual bucket survives the cut. It sorts last however big it is (that
 * is right — it is a remainder, not a competitor), which meant a plain slice
 * removed it first, and in the live data "No task" is a quarter of all hours.
 *
 * Whatever is left out is COUNTED, so the screen can say so. A chart of twelve
 * tasks under a total describing 824 of them, with nothing saying it is a
 * subset, is the reading error this whole file exists to prevent.
 */
export function chartSeries(
  rows: TimeReportResultRow[],
  outerLevel: TimeReportGroupByLevel,
  limit: number = CHART_ROWS,
): ChartSeries {
  const totals = outerTotals(rows, outerLevel);

  if (outerLevel === 'day' || outerLevel === 'week') {
    return { bars: [...totals].sort((a, b) => a.key.localeCompare(b.key)), hiddenCount: 0 };
  }
  if (totals.length <= limit) return { bars: totals, hiddenCount: 0 };

  const residual = totals.find((total) => total.key === '');
  const ranked = totals.filter((total) => total.key !== '');
  const bars = residual ? [...ranked.slice(0, limit - 1), residual] : ranked.slice(0, limit);
  return { bars, hiddenCount: totals.length - bars.length };
}

// ---------------------------------------------------------------------------
// What the drafter could not place
// ---------------------------------------------------------------------------

/**
 * `filters.departmentIds` → "Departments".
 *
 * An unknown field prints its raw path rather than a guessed name: the drafter
 * may learn to flag something this screen has not heard of, and "Something"
 * would be worse than the path.
 */
export function unresolvedFieldLabel(field: string): string {
  const key = field.startsWith('filters.') ? field.slice('filters.'.length) : field;
  // Not a chip, so `FILTER_BY_KEY` has never heard of it and the reader would
  // be handed the raw path. [HW-850]
  if (key === 'completedPeriodsOnly') return 'Completed periods only';
  const descriptor = FILTER_BY_KEY.get(key as ReportFilterKey);
  return descriptor?.label ?? field;
}

// ---------------------------------------------------------------------------
// The export
// ---------------------------------------------------------------------------

export interface BuilderExportInput {
  config: TimeReportConfig;
  window: { from: string; to: string };
  totalSeconds: number;
  rows: TimeReportResultRow[];
  generatedOn: Date;
  /**
   * The boundary "completed periods only" stopped the window at, when it did.
   *
   * The screen says this in a line above the table; the file leaves the building
   * without it, and a shorter window with no account of itself is exactly what
   * this ticket exists to prevent. Server's answer, never re-derived here.
   */
  openPeriodExcluded?: { monthLabel: string } | null;
  /**
   * The option lists the screen has loaded, so ids can be printed as names.
   *
   * Optional, and the filters are described either way: a document that could
   * not name a value still says the dimension is narrowing the report. The
   * caller cannot silence the block by forgetting an argument.
   */
  filterLabels?: FilterOptionLabels;
}

const EXPORT_STYLE = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1a1a1a; font-size: 13px; }
  .page { padding: 48px; }
  .report-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2px solid #21CE99; }
  .report-header h1 { font-size: 18px; font-weight: 600; margin-bottom: 4px; }
  .report-header p { font-size: 12px; color: #666; line-height: 1.5; }
  .summary { display: flex; gap: 16px; margin-bottom: 20px; }
  .summary-card { flex: 1; padding: 16px 20px; border-radius: 8px; background: #f8fafa; border: 1px solid #e8edec; }
  .summary-card .label { font-size: 11px; color: #007553; font-weight: 500; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
  .summary-card .value { font-size: 22px; font-weight: 600; }
  .summary-card .sub { font-size: 11px; color: #888; margin-top: 2px; }
  .note { font-size: 11px; color: #666; margin-bottom: 20px; line-height: 1.5; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  thead th { text-align: left; padding: 10px 14px; background: #f1f5f4; border-bottom: 2px solid #d4dedd; font-weight: 600; color: #556; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
  th.num, td.num { text-align: right; }
  tbody td { padding: 8px 14px; border-bottom: 1px solid #eef1f0; color: #333; }
  tbody tr.subtotal td { background: #f8fafa; font-weight: 600; color: #1a1a1a; border-top: 1px solid #d4dedd; }
  tbody td.nested { padding-left: 32px; }
  tfoot td { padding: 10px 14px; background: #f1f5f4; font-weight: 600; border-top: 2px solid #d4dedd; }
  .report-footer { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e8edec; display: flex; justify-content: space-between; font-size: 10px; color: #999; }
  @media print { .page { padding: 24px; } .summary-card { break-inside: avoid; } }
`;

function metricHeadings(metrics: TimeReportMetric[]): string[] {
  return metrics.map((metric) => REPORT_METRICS.find((m) => m.metric === metric)?.label ?? metric);
}

function metricCells(row: TimeReportResultRow, metrics: TimeReportMetric[]): string[] {
  return metrics.map((metric) => {
    if (metric === 'hours') return formatHours(row.totalSeconds);
    if (metric === 'share') return `${row.sharePercent}%`;
    return formatHours(row.dailyAverageSeconds);
  });
}

/** One column of the built report as a spreadsheet. */
export interface BuilderSheetColumn {
  header: string;
  /** Column width in characters, as a spreadsheet counts them. */
  width: number;
  /**
   * Whether the cells are percentages.
   *
   * The value written is the bare number 15, so the column can be worked with
   * — which leaves nothing in the column saying it is not 15 of whatever the
   * neighbouring Hours column holds. A workbook can put the sign back with a
   * number format; a CSV cannot, and there the heading is all there is.
   */
  percent?: boolean;
}

/** One row of the sheet, and whether it is a group's total or one of its rows. */
export interface BuilderSheetRow {
  /** Cells in column order, aligned with `columns` by index. */
  cells: Array<string | number>;
  /**
   * A group's own total.
   *
   * Carried rather than inferred because the workbook writer PAINTS these, and
   * nothing in the cells themselves marks one: a group total and a row whose
   * inner cell is a residual both have text in the first column and something
   * blank-looking in the second. In a plain CSV, which cannot carry a fill,
   * this is what the reader has instead — which is the honest reason the CSV
   * still repeats the group's name on every row.
   */
  subtotal: boolean;
}

export interface BuilderSheet {
  columns: BuilderSheetColumn[];
  /**
   * How many of the columns are grouping levels; the rest are metrics.
   *
   * Reported rather than left for the writer to work out from the config,
   * which it does not have — and the two must not disagree, because this is
   * what decides which cells align right. A label pushed to the right edge of
   * its column reads as a number.
   */
  groupColumnCount: number;
  rows: BuilderSheetRow[];
  /**
   * The grand total, in the same columns — the sheet's last row.
   *
   * A row rather than a footer, because a sheet has no footer. It has to be
   * here: the screen shows a total and the printable document carries one in
   * its `<tfoot>`, so the export whose whole purpose is adding hours up would
   * otherwise be the only one of the three with no line saying what they came
   * to.
   */
  total: Array<string | number>;
  /** Without an extension — the caller's chosen format decides that. */
  filename: string;
  /** The sheet's tab name, and the workbook's title row. */
  title: string;
  /**
   * What shaped the report, as label/text lines above the table: the window,
   * the part of it still open, the filters, and the notices the PDF prints.
   *
   * The PDF names the filters since `5c7323e4`, before either spreadsheet
   * existed, and the part still open since HW-850; the spreadsheets never said
   * either, so a report of two employment types or of August alone left as a
   * file indistinguishable from the whole company's, with the dates in the file
   * name only — and a file name is the first thing lost when a file is
   * forwarded. [HW-953]
   */
  about: BuilderSheetNote[];
}

/** One line of what shaped a report: "Period" and its dates, for instance. */
export interface BuilderSheetNote {
  label: string;
  text: string;
}

/** Spelled once, so the PDF, the workbook and the CSV say it the same way. */
const NO_FILTERS_TEXT = 'every hour logged in this window.';

function openPeriodText(openPeriodExcluded: { monthLabel: string }): string {
  return `${openPeriodExcluded.monthLabel} onward is still open and is not counted`;
}

/** The day a document was made, on the UTC calendar every export reads. */
export function generatedOnText(generatedOn: Date): string {
  return generatedOn.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * What shaped a built report, in the words its PDF uses.
 *
 * The texts come from what the PDF prints — `describeFilters`, the notices, the
 * window and the open-period sentence — but the PDF still lays its own notes
 * out in `buildBuilderReportHtml`, so a note added there does not reach the
 * spreadsheet by itself. The export tests compare every note the PDF prints as
 * a `p.note` in both directions, and that comparison is what fails when they
 * drift — the drift this ticket is about. A line the PDF adds elsewhere, in its
 * header or its summary cards, is compared one way only. [HW-953]
 */
function describeReport(input: BuilderExportInput): BuilderSheetNote[] {
  const { config, window, generatedOn, filterLabels, openPeriodExcluded } = input;
  const applied = describeFilters(config, filterLabels);
  const notes: BuilderSheetNote[] = [{ label: 'Period', text: formatWindow(window) }];
  if (openPeriodExcluded) {
    notes.push({ label: 'Not counted', text: openPeriodText(openPeriodExcluded) });
  }
  notes.push({ label: 'Filters', text: filtersText(applied) });
  if (config.groupBy.includes('week')) {
    notes.push({
      label: 'Note',
      text: openPeriodExcluded ? WEEK_WINDOW_CUT_NOTICE : WEEK_WINDOW_NOTICE,
    });
  }
  if (usesPeopleAttributes(config)) notes.push({ label: 'Note', text: PEOPLE_ATTRIBUTE_NOTICE });
  // "(UTC)" is honest — the day is taken on the UTC calendar, as the PDF's is —
  // and it is what keeps the cell text: Excel opening the CSV reads a bare
  // "September 30, 2026" as a date and shows it as ######## in a column too
  // narrow for it.
  notes.push({ label: 'Generated', text: `${generatedOnText(generatedOn)} (UTC)` });
  return notes;
}

function filtersText(applied: AppliedFilter[]): string {
  return applied.length
    ? applied.map((filter) => `${filter.label}: ${filter.text}`).join('; ')
    : `No filters — ${NO_FILTERS_TEXT}`;
}

/**
 * What shaped the Reports screen's entry list, for its three exports.
 *
 * Not a built report — a window, at most one project and at most one person —
 * but its files leave the screen the same way, and a list narrowed to one
 * project read as if the company had worked on nothing else. Worded by the same
 * `describeFilters`, so the project and people lines read as the Builder's do.
 * [HW-953, HW-955]
 */
export function describeEntryList(
  input: EntryListScope & { windowLabel: string; generatedOn: Date }
): BuilderSheetNote[] {
  return [
    { label: 'Period', text: input.windowLabel },
    { label: 'Filters', text: filtersText(entryListFilters(input)) },
    { label: 'Generated', text: `${generatedOnText(input.generatedOn)} (UTC)` },
  ];
}

/**
 * The entry list's PDF line for its filters: "Filters — Projects: Atlas", or
 * "No filters — …", which says itself. Worded as the Builder's PDF words it,
 * and decided by the same filters as the spreadsheet lines. [HW-953]
 */
export function entryListFiltersLine(scope: EntryListScope): string {
  const applied = entryListFilters(scope);
  return applied.length ? `Filters — ${filtersText(applied)}` : filtersText(applied);
}

/**
 * The one project and the one person, if any, the Reports screen's entry list
 * was narrowed to. [HW-953, HW-955]
 */
export interface EntryListScope {
  projectId: string | null;
  projectNames: ReadonlyMap<string, string>;
  employeeId: string | null;
  employeeNames: ReadonlyMap<string, string>;
}

function entryListFilters({
  projectId,
  projectNames,
  employeeId,
  employeeNames,
}: EntryListScope): AppliedFilter[] {
  const config: TimeReportConfig = {
    ...cloneConfig(DEFAULT_TIME_REPORT_CONFIG),
    filters: {
      billable: 'all',
      ...(projectId ? { projectIds: [projectId] } : {}),
      ...(employeeId ? { employeeIds: [employeeId] } : {}),
    },
  };
  return describeFilters(config, { projects: projectNames, employees: employeeNames });
}

/**
 * The one person a report was narrowed to, by name — or null when it names
 * nobody, several people, or someone whose name is not loaded.
 */
export function onePersonName(
  employeeIds: readonly string[] | undefined,
  names: ReadonlyMap<string, string> | undefined
): string | null {
  if (employeeIds?.length !== 1) return null;
  return names?.get(employeeIds[0]) ?? null;
}

/**
 * The tail a file name carries for the one person its report was narrowed to:
 * "-allene-bins", or nothing. Without it every per-person export of a month
 * downloads under the same name, and accounting exporting five people in a row
 * gets "(1)", "(2)"… told apart only by opening them. Letters of any script are
 * kept, so a Cyrillic name is not reduced to dashes — and their marks with them,
 * composed once lower-cased (lower-casing can itself decompose, as "İ" does): a
 * name stored decomposed ("e" + U+0301, as a sync or a Mac
 * keyboard can write it) would otherwise break into "rene-e", and Indic vowel
 * signs are marks even when composed. [HW-955]
 */
export function personFileSuffix(name: string | null): string {
  const slug = (name ?? '')
    .toLowerCase()
    .normalize('NFC')
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `-${slug}` : '';
}

/**
 * A built report as spreadsheet rows.
 *
 * The Builder had no spreadsheet at all: its one Export button opened a print
 * window, so the only way to get these hours into Excel was the Reports
 * screen's entry list — one row per LOG ENTRY which, measured on dev, dropped
 * the per-person totals its own screen shows. Forty-nine log lines and a
 * single grand total is not a report anybody can hand to accounting. That
 * screen's summarized views now export through this builder too. [HW-877]
 *
 * Every number is written as a NUMBER, not as its formatted text, because the
 * whole reason a spreadsheet was asked for over the PDF is that the recipient
 * adds these up themselves — and `downloadSpreadsheet` documents that a number
 * survives as one even through CSV's forced quoting. Hours are the report's own
 * seconds converted here rather than re-parsed from `formatHours`, so a cell
 * cannot disagree with the screen by a rounding step.
 *
 * The shape follows the on-screen table exactly, group totals included: a
 * summarized report whose export is a different shape is the defect this
 * ticket is about.
 */
export function buildBuilderSheet(input: BuilderExportInput): BuilderSheet {
  const { config, window, rows, totalSeconds } = input;
  const groupBy = config.groupBy;
  const metrics = config.metrics;
  const person = personFileSuffix(
    onePersonName(config.filters.employeeIds, input.filterLabels?.employees)
  );

  const columns: BuilderSheetColumn[] = [
    ...groupBy.map((level) => ({ header: levelLabel(level), width: 26 })),
    ...metrics.map((metric, index) => ({
      header: metricHeadings(metrics)[index],
      width: 16,
      percent: metric === 'share',
    })),
  ];

  const sheetRows: BuilderSheetRow[] = rows.map((row) => ({
    subtotal: row.subtotal,
    cells: [
      ...groupBy.map((level, index) =>
        // A group total's inner cell is blank because it HAS no inner group —
        // not the residual "No project", which is what `rowLabel` would print.
        // `nested` is false here: a spreadsheet keeps the name on every row.
        blankCell(row, false, index) ? '' : rowLabel(level, row.label[index] ?? '')
      ),
      ...metrics.map((metric) => {
        if (metric === 'hours') return roundHours(row.totalSeconds);
        if (metric === 'share') return row.sharePercent;
        return roundHours(row.dailyAverageSeconds);
      }),
    ],
  }));

  return {
    columns,
    groupColumnCount: groupBy.length,
    rows: sheetRows,
    // The report's own total, from the server, exactly as the printable
    // document's footer takes it — never the column added up again, which is
    // the disagreement `totalsAgree` exists to catch rather than to create.
    total: [
      'Total',
      ...groupBy.slice(1).map(() => ''),
      ...metrics.map((metric) => {
        if (metric === 'hours') return roundHours(totalSeconds);
        // 100 as a NUMBER, matching the share column's own cells, so the
        // column has one type from top to bottom. A daily average over the
        // whole report would divide by a day count nothing measured, so the
        // footer leaves it blank — as the printed document does.
        if (metric === 'share') return 100;
        return '';
      }),
    ],
    // The window the SERVER resolved, so the filename names the same dates the
    // document's heading does — a week axis grows the window past the request.
    // `window.to` precedes `window.from` when the completed-periods filter left
    // nothing to count, and a file called `…2026-09-01_2026-08-31` reads as a bug
    // in whatever produced it. Name that one for what it holds. [HW-850]
    // The one person it was narrowed to closes the name. [HW-955]
    filename:
      window.to < window.from
        ? `time-report-${groupBy.join('-')}-${window.from}_nothing-completed${person}`
        : `time-report-${groupBy.join('-')}-${window.from}_${window.to}${person}`,
    title: `Time report by ${groupBy.map((level) => levelLabel(level).toLowerCase()).join(' and ')}`,
    about: describeReport(input),
  };
}

/** Hours as a spreadsheet cell: a number, to two places, never a string. */
function roundHours(seconds: number): number {
  return Math.round((seconds / 3600) * 100) / 100;
}

/**
 * The built report as a printable document.
 *
 * `escapeHtml`, `formatHours` and `formatDuration` come from `report-print.ts`
 * — the same three the Reports tab's PDF uses, so one number is never written
 * two ways across two exports. The table itself is this file's: the Reports
 * tab prints ENTRIES under group banners, and a built report has no entries,
 * only aggregates.
 */
export function buildBuilderReportHtml(input: BuilderExportInput): string {
  const { config, window, totalSeconds, rows, generatedOn, filterLabels, openPeriodExcluded } =
    input;
  const groupBy = config.groupBy;
  const metrics = config.metrics;

  const title = `Time report by ${groupBy.map((level) => levelLabel(level).toLowerCase()).join(' and ')}`;
  // The browser names a saved PDF after the document's <title>, so the person
  // goes there too — the heading on the page already has the Filters line to
  // say it. [HW-955]
  const person = onePersonName(config.filters.employeeIds, filterLabels?.employees);
  const documentTitle = person ? `${title} — ${person}` : title;
  const windowLabel = formatWindow(window);

  const head = [
    ...groupBy.map((level) => `<th>${escapeHtml(levelLabel(level))}</th>`),
    ...metricHeadings(metrics).map((label) => `<th class="num">${escapeHtml(label)}</th>`),
  ].join('');

  // Rows under a total line do not repeat the group's name and are indented
  // instead — the screen's rule, see `headedGroups`.
  const headed = headedGroups(rows);
  const body = rows
    .map((row) => {
      const nested = underHeader(row, headed);
      // A group total's inner cell is blanked explicitly, by `blankCell` — the
      // screen's and the spreadsheet's rule too. It does NOT empty itself: the
      // row is one key long, `row.label[1]` is undefined, and `rowLabel` turns
      // an empty label into the RESIDUAL name — so a total over untasked work
      // printed "Growth Team | No task | 47.00" directly above the real
      // residual row "Growth Team | No task | 42.00", the same words twice with
      // the larger number being the sum of the smaller. The class alone cannot
      // save it: Chrome prints without background graphics by default, so the
      // tint that marks a total is usually absent from the PDF. [HW-877]
      const keyCells = groupBy.map((level, index) => {
        if (blankCell(row, nested, index)) return '<td></td>';
        const cell = escapeHtml(rowLabel(level, row.label[index] ?? ''));
        return nested && index === 1 ? `<td class="nested">${cell}</td>` : `<td>${cell}</td>`;
      });
      const valueCells = metricCells(row, metrics).map(
        (value) => `<td class="num">${escapeHtml(value)}</td>`,
      );
      return `<tr${row.subtotal ? ' class="subtotal"' : ''}>${[...keyCells, ...valueCells].join('')}</tr>`;
    })
    .join('');

  // The footer repeats the report's own total rather than adding the column up
  // again: the headline and the axis come from one predicate server-side, and
  // a second arithmetic here could only ever disagree with it.
  const footCells = [
    `<td colspan="${groupBy.length}">Total</td>`,
    ...metrics.map((metric) =>
      metric === 'hours'
        ? `<td class="num">${formatHours(totalSeconds)}</td>`
        : metric === 'share'
          ? '<td class="num">100%</td>'
          : '<td class="num"></td>',
    ),
  ].join('');

  // Emitted from the config on every document, in both directions: a report
  // that narrows says what by, and a report that narrows nothing says THAT.
  // Silence used to mean either, which is how a 657-hour report of two
  // employment types printed as if it were August's 754-hour whole.
  const applied = describeFilters(config, filterLabels);
  const filterNote = applied.length
    ? `<p class="note"><strong>Filters</strong> — ${applied
        .map((filter) => `${escapeHtml(filter.label)}: ${escapeHtml(filter.text)}`)
        .join('; ')}</p>`
    : `<p class="note"><strong>No filters</strong> — ${NO_FILTERS_TEXT}</p>`;

  const weekNote = config.groupBy.includes('week')
    ? `<p class="note">${escapeHtml(
        openPeriodExcluded ? WEEK_WINDOW_CUT_NOTICE : WEEK_WINDOW_NOTICE,
      )}</p>`
    : '';

  const notice = usesPeopleAttributes(config)
    ? `<p class="note">${escapeHtml(PEOPLE_ATTRIBUTE_NOTICE)}</p>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<title>${escapeHtml(documentTitle)}</title>
<style>${EXPORT_STYLE}</style>
</head>
<body>
<div class="page">
  <div class="report-header">
    <div>
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(windowLabel)}</p>
    </div>
    <div style="text-align:right">
      <p>Generated ${escapeHtml(generatedOnText(generatedOn))}</p>
      <p>Hygge Software</p>
    </div>
  </div>
  <div class="summary">
    <div class="summary-card">
      <div class="label">Total</div>
      <div class="value">${formatHours(totalSeconds)}h</div>
      <div class="sub">${escapeHtml(formatDuration(totalSeconds))}</div>
    </div>
    <div class="summary-card">
      <div class="label">Period</div>
      <div class="value" style="font-size:14px">${escapeHtml(windowLabel)}</div>
      <div class="sub">${
        openPeriodExcluded
          ? `the window this total was computed over — ${escapeHtml(
              openPeriodText(openPeriodExcluded),
            )}`
          : 'the window this total was computed over'
      }</div>
    </div>
    <div class="summary-card">
      <div class="label">Rows</div>
      <div class="value">${rows.filter((row) => !row.subtotal).length}</div>
      <div class="sub">grouped by ${escapeHtml(groupBy.map(levelLabel).join(' → ').toLowerCase())}</div>
    </div>
  </div>
  ${filterNote}
  ${weekNote}
  ${notice}
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
    <tfoot><tr>${footCells}</tr></tfoot>
  </table>
  <div class="report-footer"><span>Hygge Software</span><span>${escapeHtml(windowLabel)}</span></div>
</div>
</body>
</html>`;
}
